// ============================================================
// KOT Showcase Products — "Our Work" section (main site 3001)
// Har product ka detail page /work/:slug par khulta hai.
// Content source: attached PRD documents.
// ============================================================

export interface ProductSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface Product {
  slug: string;
  name: string;
  tagline: string;
  industry: string;
  platform: string;
  image: string; // background thumbnail (src/images/projects/<slug>.png)
  summary: string;
  overview: string[];
  highlights: { label: string; value: string }[];
  sections: ProductSection[];
}

export const products: Product[] = [
  {
    slug: "deal-flow",
    name: "Deal Flow",
    tagline: "Business Management System",
    industry: "Enterprise Operations",
    platform: "Web (role-based admin panel + client portal)",
    image: "dealflow",
    summary:
      "A web-based business management system that brings sales, customer management, finance, project delivery, procurement, and support together on one role-based platform.",
    overview: [
      "Deal Flow replaces scattered spreadsheets and disconnected tools with a single system where every department works from the same data — information entered in one place is instantly available everywhere it is needed.",
      "The whole platform is built on role-based access: each staff member sees and acts on only the parts relevant to their function, while a Super Admin retains full visibility and control. No one can self-register — accounts are created by the Super Admin and scoped by role.",
    ],
    highlights: [
      { label: "Core Modules", value: "20+" },
      { label: "Staff Roles", value: "7 + Super Admin" },
      { label: "Permission Controls", value: "43 features" },
      { label: "Access Model", value: "Zero-trust RBAC" },
    ],
    sections: [
      {
        title: "How It Works",
        paragraphs: [
          "The Super Admin sets up the organization — creating staff accounts, defining roles, enabling modules, and configuring global settings. At creation, each account is assigned a role, and from that point the system enforces exactly which modules and actions that person can see.",
          "When a staff member logs in, they land directly on a dashboard and navigation set scoped to their role — a Sales Manager sees a narrower menu than the Super Admin, a Project Manager gets a delivery-focused dashboard, and so on.",
        ],
      },
      {
        title: "Role-Based Access",
        bullets: [
          "Super Admin — unrestricted access to all modules, settings, and configuration",
          "Accounts Manager — invoicing, payments, expenses, and vendor bills",
          "Sales Manager — the sales pipeline from lead through to invoice",
          "Project Manager — service delivery: projects, tasks, timesheets, milestones",
          "Support Agent — customer tickets and the knowledge base",
          "Director / Head / VP — company-wide visibility + purchase approval authority",
          "Internee — highly restricted, sees only assigned work",
        ],
      },
      {
        title: "Key Modules",
        bullets: [
          "Dashboard — summary cards, delivery table, financial overviews, leads & payments charts",
          "Customers — master records with contacts, notes, and client-portal access",
          "Sales — Proposals, Estimates, Invoices, Payments, Credit Notes, Items catalogue",
          "Projects — list & Gantt views, tasks, timesheets, milestones, billing types",
          "Purchase — full procurement lifecycle: requests, quotations, orders, invoices, debit notes",
          "Support — ticketing lifecycle (Open → In Progress → Answered → Closed)",
          "Leads & Estimate Requests — pipeline with Kanban views and one-click conversion",
          "Workflow Automation, Contracts, Subscriptions, Expenses, Reports, and Setup",
        ],
      },
      {
        title: "End-to-End Business Flows",
        paragraphs: [
          "Sales-to-Delivery: the Sales Manager captures a lead, sends a proposal, converts it to an estimate and invoice, then creates the project and hands it to a Project Manager for delivery. The Accounts Manager records payments to close the loop.",
          "Purchase & Approval: a staff member raises a purchase request, senior leadership (Director / Head / VP) approves it, it becomes a purchase order, and the Accounts Manager records the resulting vendor invoice.",
        ],
      },
    ],
  },
  {
    slug: "cartify",
    name: "Cartify",
    tagline: "E-Commerce Platform",
    industry: "Retail & Commerce",
    platform: "Web (admin panel + customer storefront)",
    image: "cartify",
    summary:
      "A ready-to-use e-commerce platform that gives a store owner catalog management, order processing, marketing tools, and a public storefront in one connected system — no need to build a website and checkout from scratch.",
    overview: [
      "Cartify is the customer-facing brand of a two-part system: an Admin Panel that serves as the internal control room, and a Customer Storefront — the public shopping site that displays the catalog and content configured through the admin.",
      "Both areas share a single account and login system, but what a person can do depends entirely on their role. It ships as a single-vendor store with role-restricted staff accounts and self-registering customers.",
    ],
    highlights: [
      { label: "Admin Modules", value: "18" },
      { label: "Payment Gateways", value: "17" },
      { label: "User Roles", value: "Admin · Staff · Customer" },
      { label: "Product Engine", value: "Variations + Options" },
    ],
    sections: [
      {
        title: "How It Works",
        paragraphs: [
          "The store owner (Super Admin) sets up the catalog and configures the storefront through the Admin Panel. Staff accounts are created with role-restricted access — for example a 'Catalog Manager' who can only manage products.",
          "Customers self-register on the storefront in seconds and are automatically placed in the Customer role — they browse, add to cart, check out, and manage their own orders from My Account, with no path into the admin panel.",
        ],
      },
      {
        title: "Products & Catalog",
        bullets: [
          "Categories, Brands, Tags — the store's navigation and filtering structure",
          "Attributes & Attribute Sets — the three-layer specifications system",
          "Variations — distinct SKUs per option (size, color) with their own price and stock",
          "Options — paid add-ons like gift wrap that don't create a separate SKU",
          "Create Product form — pricing, special prices, inventory tracking, SEO, linked products",
          "Reviews — customer ratings moderated by the admin",
        ],
      },
      {
        title: "Sales, Marketing & Content",
        bullets: [
          "Orders — full lifecycle (Pending → Processing → Shipped → Delivered) + transactions",
          "Flash Sales — limited-time, limited-stock deals with countdown timers",
          "Coupons — percentage or fixed discount codes with usage limits and restrictions",
          "Pages, Menus, Blog — static content, navigation, and SEO articles",
          "Import — bulk product creation from CSV/Excel with image ZIP",
          "Appearance — homepage slider + a 12-section homepage builder",
        ],
      },
      {
        title: "Storefront Experience",
        paragraphs: [
          "The public store carries a header with search and category navigation, live Compare / Wishlist / Cart counters, and a footer with contact and account links.",
          "The My Account panel gives each customer their own dashboard, orders, downloads, wishlist, reviews, addresses, and profile — plus a contact page, product compare, and a slide-in cart drawer.",
        ],
      },
    ],
  },
  {
    slug: "lms",
    name: "K-Edu LMS",
    tagline: "Learning Management System",
    industry: "Education & Training",
    platform: "Web (responsive, role-based)",
    image: "lms",
    summary:
      "A web-based learning platform where instructors build courses and assessments, learners enroll and study, and administrators manage users, content, and reporting — bringing content, assessment, communication, and reporting into one place.",
    overview: [
      "Learning delivery today is fragmented across video tools, file shares, spreadsheets, and email. The LMS is the dependable backbone for any learning program — a place where creating a course is fast, learners always know what to do next, and every stakeholder can see real progress without chasing it.",
      "It works equally well for a school, a university, or a corporate training team, with role-based access for administrators, instructors, and learners.",
    ],
    highlights: [
      { label: "Course Setup", value: "Under 30 min" },
      { label: "Target Uptime", value: "99.9%" },
      { label: "Roles", value: "Admin · Instructor · Learner" },
      { label: "Assessments", value: "Quizzes + Assignments" },
    ],
    sections: [
      {
        title: "How It Works",
        paragraphs: [
          "Instructors assemble a course from modules and lessons, attach content (documents, video, links, quizzes), preview it as a draft, and publish it to make it visible to learners.",
          "Learners enroll — manually, in bulk, or via a self-enrollment code — then work through lessons, take assessments, and track their own progress on a dashboard of what is due. Administrators manage users and roles and pull completion reports.",
        ],
      },
      {
        title: "Who Uses It",
        bullets: [
          "Administrator — sets up the institution, manages users & roles, oversees reporting",
          "Instructor — creates courses, uploads materials, builds quizzes, grades submissions",
          "Learner — enrolls, consumes content, takes assessments, tracks progress",
        ],
      },
      {
        title: "Core Features",
        bullets: [
          "User & Role Management — create, import, and manage users with role-based access",
          "Course Builder — structure modules and lessons, publish / unpublish",
          "Content Delivery — documents, video, links, and downloadable resources",
          "Enrollment — manual, bulk, or self-enrollment codes",
          "Assessments & Gradebook — quizzes, assignments, auto-grading, feedback, export",
          "Progress Tracking — lesson completion and course-completion percentage",
          "Notifications & Reporting — deadline/grade alerts and role-based dashboards",
        ],
      },
      {
        title: "Built To Scale",
        bullets: [
          "Supports 10,000+ concurrent active users",
          "Pages load within 3 seconds for 95% of requests",
          "Encrypted (HTTPS/TLS) with strong password hashing",
          "WCAG 2.1 AA accessibility, responsive from 360px up",
          "Daily backups with a 24-hour recovery point objective",
        ],
      },
    ],
  },
];

export const getProduct = (slug: string): Product | undefined =>
  products.find((p) => p.slug === slug);
